import type { ReactNode } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { signOut } from '../features/auth/auth-api';
import { sessionQueryKey } from '../features/auth/session';
import type { CurrentUser } from '../features/auth/session';
import { usePrefersReducedMotion } from '../lib/use-prefers-reduced-motion';
import { useUiStore } from '../state/ui-store';

import { GlobalSearch } from './GlobalSearch';
import { MOBILE_PRIMARY_PATHS, NAV_ITEMS, filterNavItemsByPermissions } from './nav-items';

export interface AppShellProps {
  user: CurrentUser;
  children: ReactNode;
}

/**
 * Powłoka aplikacji (§6: nawigacja boczna desktop / dolna telefon, wspólny nagłówek).
 *
 * Nawigacja filtruje pozycje wg `user.permissions` (§4 „efektywne uprawnienia są sumą") —
 * `filterNavItemsByPermissions` (`./nav-items.ts`). Dane sesji pochodzą z route contextu trasy
 * chronionej (`protectedLayoutRoute.beforeLoad`, `router.tsx`), więc są już wczytane, zanim ten
 * komponent się zamontuje — bez dodatkowego stanu ładowania i bez migotania (§7.2).
 */
export function AppShell({ user, children }: AppShellProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isMobileMoreOpen = useUiStore((state) => state.isMobileMoreOpen);
  const toggleMobileMore = useUiStore((state) => state.toggleMobileMore);
  const closeMobileMore = useUiStore((state) => state.closeMobileMore);
  const prefersReducedMotion = usePrefersReducedMotion();

  const visibleItems = filterNavItemsByPermissions(NAV_ITEMS, user.permissions);
  const mobilePrimaryItems = visibleItems.filter((item) => MOBILE_PRIMARY_PATHS.includes(item.to));
  const mobileMoreItems = visibleItems.filter((item) => !MOBILE_PRIMARY_PATHS.includes(item.to));

  const signOutMutation = useMutation({
    mutationFn: signOut,
    onSettled: async () => {
      // Wylogowanie unieważnia sesję po stronie serwera (§17.2) — po stronie klienta cache
      // sesji jest zerowany od razu (`setQueryData`), żeby `AppShell`/trasa chroniona nie
      // pokazały przez ułamek sekundy starego, uwierzytelnionego stanu (§7.2 „bez migotania").
      queryClient.setQueryData(sessionQueryKey, null);
      closeMobileMore();
      await navigate({ to: '/login' });
    },
  });

  const handleSignOut = (): void => {
    signOutMutation.mutate();
  };

  return (
    <div className="flex min-h-screen flex-col bg-bg text-ink md:flex-row">
      <a
        href="#glowna-tresc"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-surface focus:px-3 focus:py-2"
      >
        Przejdź do treści
      </a>

      <nav
        aria-label="Nawigacja główna"
        className="hidden w-60 shrink-0 flex-col gap-1 border-r border-border bg-surface p-4 md:flex"
      >
        <span className="font-heading text-lg text-ink">Archiwum</span>
        <ul className="mt-4 flex flex-col gap-1">
          {visibleItems.map((item) => (
            <li key={item.to}>
              <Link
                to={item.to}
                className="flex min-h-11 items-center rounded px-3 text-ink hover:bg-bg"
                activeProps={{ className: 'nav-active', 'aria-current': 'page' }}
              >
                {item.label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      <div className="flex min-h-screen flex-1 flex-col">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-surface px-4 py-3">
          <span className="font-heading text-xl text-ink">Rodzinne Archiwum</span>

          <GlobalSearch />

          <div className="flex items-center gap-3">
            <span className="text-sm text-ink">
              <span className="sr-only">Zalogowano jako</span>
              {user.email}
            </span>
            <button
              type="button"
              onClick={handleSignOut}
              disabled={signOutMutation.isPending}
              className="h-11 min-w-11 rounded border border-border-strong px-3 text-sm text-ink disabled:opacity-70"
            >
              Wyloguj się
            </button>
          </div>
        </header>

        <main id="glowna-tresc" className="flex-1 p-4 pb-24 md:pb-4">
          {children}
        </main>
      </div>

      <nav
        aria-label="Nawigacja główna (telefon)"
        className="fixed inset-x-0 bottom-0 z-40 flex border-t border-border bg-surface md:hidden"
      >
        {mobilePrimaryItems.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className="flex min-h-11 flex-1 flex-col items-center justify-center py-2 text-xs text-ink"
            activeProps={{ className: 'nav-active', 'aria-current': 'page' }}
          >
            {item.label}
          </Link>
        ))}
        {mobileMoreItems.length > 0 ? (
          <button
            type="button"
            className="flex min-h-11 flex-1 flex-col items-center justify-center py-2 text-xs text-ink"
            aria-expanded={isMobileMoreOpen}
            aria-controls="mobile-more-panel"
            onClick={toggleMobileMore}
          >
            Więcej
          </button>
        ) : null}
      </nav>

      {isMobileMoreOpen ? (
        <div
          id="mobile-more-panel"
          role="dialog"
          aria-label="Więcej pozycji nawigacji"
          data-motion={prefersReducedMotion ? 'reduced' : 'full'}
          className={`fixed inset-x-0 bottom-14 z-40 rounded-t border border-border bg-surface p-4 md:hidden ${
            prefersReducedMotion ? '' : 'transition-transform duration-200 ease-out'
          }`}
        >
          <ul className="flex flex-col gap-1">
            {mobileMoreItems.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  onClick={closeMobileMore}
                  className="flex min-h-11 items-center rounded px-3 text-ink"
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
